import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, LogOut, Save, Trash2, X, Eye, EyeOff } from "lucide-react";
import ThemeSelector from "../components/ThemeSelector";
import NetworkBrandingSettings from "../components/NetworkBrandingSettings";
import {
    fetchClient,
    updateClient,
    deleteClient,
    fetchClientAccessOverview,
    createClientAccessRequest,
    resolveClientAccessInvite,
    sendClientAccessRequestMessage,
    disconnectClientAccessNetwork,
    dismissRemovedNetworkBanner,
} from "../services/profile";
import { logout } from "../services/session";
import { isCapacitorRuntime } from "../config/vars";
import {
    getAppLockEnabled,
    isBiometricLockAvailable,
    getBiometricLockStatus,
    formatBiometricUnavailableMessage,
    markAppLockSkipPrompt,
    setAppLockEnabled,
    verifyBiometricLock,
} from "../utils/appLock";
import {
    getWeatherCity,
    getWeatherEnabled,
    getWeatherShowHumidity,
    getWeatherShowPressure,
    getWeatherShowWind,
    setWeatherCity,
    setWeatherEnabled,
    setWeatherShowHumidity,
    setWeatherShowPressure,
    setWeatherShowWind,
} from "../utils/weather";

const ClientProfile = ({ onPasswordChanged }) => {
    const navigate = useNavigate();
    const [loggingOut, setLoggingOut] = useState(false);
    const [clientData, setClientData] = useState(null);
    const [editedData, setEditedData] = useState({
        name: "",
        descriptions: "",
        car_number: "",
        current_password: "",
        new_password: "",
    });
    const [editing, setEditing] = useState(false);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [appLockReady, setAppLockReady] = useState(false);
    const [appLockEnabled, setAppLockEnabledState] = useState(false);
    const [appLockAvailable, setAppLockAvailable] = useState(false);
    const [appLockMessage, setAppLockMessage] = useState("");

    const [weatherEnabled, setWeatherEnabledState] = useState(() => getWeatherEnabled());
    const [weatherCityDraft, setWeatherCityDraft] = useState(() => getWeatherCity());
    const [weatherShowWind, setWeatherShowWindState] = useState(() => getWeatherShowWind());
    const [weatherShowPressure, setWeatherShowPressureState] = useState(() => getWeatherShowPressure());
    const [weatherShowHumidity, setWeatherShowHumidityState] = useState(() => getWeatherShowHumidity());
    const [accessOverview, setAccessOverview] = useState({
        active_context_id: null,
        contexts: [],
        requests: [],
        options: { networks: [], services: [], locations: [] },
    });
    const [creatingAccessRequest, setCreatingAccessRequest] = useState(false);
    const [disconnectingNetworkId, setDisconnectingNetworkId] = useState(null);
    const [sendingAccessMessageId, setSendingAccessMessageId] = useState(null);
    const [accessMessageDrafts, setAccessMessageDrafts] = useState({});
    const [resolvingInvite, setResolvingInvite] = useState(false);
    const [resolvedInvite, setResolvedInvite] = useState(null);
    const [accessPanels, setAccessPanels] = useState({
        networks: false,
        create: false,
        requests: false,
    });
    const [newAccessRequest, setNewAccessRequest] = useState({
        invite_code: "",
        service_id: "",
        descriptions: "",
    });
    const loadAccessOverview = useCallback(async () => {
        const overview = await fetchClientAccessOverview();
        setAccessOverview(overview || {
            active_context_id: null,
            contexts: [],
            requests: [],
            options: { networks: [], services: [], locations: [] },
        });
    }, []);


    // 🔁 слухаємо зміну розміру екрана
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        let cancelled = false;

        if (!isCapacitorRuntime()) {
            setAppLockReady(true);
            return;
        }

        (async () => {
            try {
                const [enabled, available] = await Promise.all([
                    getAppLockEnabled(),
                    isBiometricLockAvailable(),
                ]);
                if (cancelled) return;
                setAppLockEnabledState(enabled);
                setAppLockAvailable(available);
            } catch (_) {
                // ignore
            } finally {
                if (!cancelled) setAppLockReady(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    const handleToggleAppLock = async (e) => {
        const next = !!e.target.checked;
        setAppLockMessage("");

        if (!isCapacitorRuntime()) return;

        if (next) {
            try {
                const status = await getBiometricLockStatus();
                setAppLockAvailable(status.isAvailable);
                if (!status.isAvailable) {
                    setAppLockMessage(formatBiometricUnavailableMessage(status.code));
                    return;
                }

                // Skip gate BEFORE system biometric UI (it backgrounds the WebView).
                markAppLockSkipPrompt(15000);
                await verifyBiometricLock({
                    title: "Увімкнути блокування",
                    subtitle: "Підтвердьте особу",
                });
                await setAppLockEnabled(true);
                setAppLockEnabledState(true);
                setAppLockMessage("✅ Блокування увімкнено");
            } catch (err) {
                setAppLockMessage(`❌ ${err?.message || "Не вдалося увімкнути"}`);
            }
            return;
        }

        try {
            await setAppLockEnabled(false);
            setAppLockEnabledState(false);
            setAppLockMessage("✅ Блокування вимкнено");
        } catch (err) {
            setAppLockMessage(`❌ ${err?.message || "Не вдалося вимкнути"}`);
        }
    };

    const handleToggleWeather = (e) => {
        const next = !!e.target.checked;
        setWeatherEnabledState(next);
        setWeatherEnabled(next);
    };

    const handleToggleWeatherShowWind = (e) => {
        const next = !!e.target.checked;
        setWeatherShowWindState(next);
        setWeatherShowWind(next);
    };

    const handleToggleWeatherShowPressure = (e) => {
        const next = !!e.target.checked;
        setWeatherShowPressureState(next);
        setWeatherShowPressure(next);
    };

    const handleToggleWeatherShowHumidity = (e) => {
        const next = !!e.target.checked;
        setWeatherShowHumidityState(next);
        setWeatherShowHumidity(next);
    };

    const commitWeatherCity = () => {
        setWeatherCity(weatherCityDraft);
    };

    const loadClient = useCallback(async () => {
        try {
            const [me, overview] = await Promise.all([
                fetchClient(),
                fetchClientAccessOverview(),
            ]);
            if (!me) throw new Error("Дані клієнта не знайдено");
            setClientData(me);
            setAccessOverview(overview || {
                active_context_id: null,
                contexts: [],
                requests: [],
                options: { networks: [], services: [], locations: [] },
            });
            if (onPasswordChanged) {
                onPasswordChanged(me.force_password_change);
            }
            setEditedData({
                name: me.name || "",
                descriptions: me.descriptions || "",
                car_number: me.car_number || "",
                current_password: "",
                new_password: "",
            });
        } catch (e) {
            setMessage(`❌ ${e.message}`);
        } finally {
            setLoading(false);
        }
    }, [onPasswordChanged]);

    useEffect(() => {
        loadClient();
    }, [loadClient]);

    const handleEdit = () => {
        setEditing(true);
        setMessage("");
    };

    const handleCancel = () => {
        if (clientData) {
            setEditedData({
                name: clientData.name || "",
                descriptions: clientData.descriptions || "",
                car_number: clientData.car_number || "",
                current_password: "",
                new_password: "",
            });
        }
        setEditing(false);
        setMessage("");
        setShowCurrentPassword(false);
        setShowNewPassword(false);
    };

    const handleSave = async () => {
        setMessage("");
        try {
            const payload = {
                name: editedData.name,
                descriptions: editedData.descriptions,
                car_number: editedData.car_number,
            };
            if (editedData.new_password) {
                payload.current_password = editedData.current_password;
                payload.new_password = editedData.new_password;
            }
            const updated = await updateClient(payload);
            setClientData(updated);
            if (onPasswordChanged) {
                onPasswordChanged(updated.force_password_change);
            }
            setEditing(false);
            setEditedData({
                name: updated.name || "",
                descriptions: updated.descriptions || "",
                car_number: updated.car_number || "",
                current_password: "",
                new_password: "",
            });
            setShowCurrentPassword(false);
            setShowNewPassword(false);
            setMessage("✅ Профіль оновлено успішно");
        } catch (e) {
            setMessage(`❌ ${e.message}`);
        }
    };

    const handleDeleteAccount = async () => {
        if (!window.confirm("Ви впевнені, що хочете видалити акаунт? Це дія незворотна.")) return;
        setMessage("");
        try {
            await deleteClient();
            setMessage("✅ Акаунт видалено. Ви будете перенаправлені на сторінку входу.");
            setTimeout(async () => {
                try {
                    await logout();
                } catch (_) {
                    // ignore
                }
                window.location.href = "/";
            }, 2000);
        } catch (e) {
            setMessage(`❌ ${e.message}`);
        }
    };

    const handleLogout = useCallback(async () => {
        if (loggingOut) return;
        const confirmed = window.confirm("Вийти з акаунта?");
        if (!confirmed) return;
        setLoggingOut(true);
        try {
            await logout();
        } catch (_) {
            // clear local session even if API fails
        }
        localStorage.clear();
        navigate("/");
    }, [loggingOut, navigate]);

    const accessContexts = accessOverview?.contexts || [];
    const accessRequests = accessOverview?.requests || [];
    const pendingDisconnectByNetworkId = accessRequests.reduce((acc, request) => {
        if (request?.request_type === "disconnect" && request?.status === "pending" && request?.network_id) {
            acc[String(request.network_id)] = true;
        }
        return acc;
    }, {});
    const filteredServices = Array.isArray(resolvedInvite?.services) ? resolvedInvite.services : [];
    const accessNetworks = Object.values(
        accessContexts.reduce((acc, context) => {
            const networkKey = context?.network_id ? String(context.network_id) : `context-${context.id}`;
            if (!acc[networkKey]) {
                acc[networkKey] = {
                    network_id: context?.network_id || null,
                    network_name: context?.network_name || "Без назви мережі",
                    is_primary: false,
                    services: [],
                    locations: [],
                    contexts: [],
                };
            }
            acc[networkKey].contexts.push(context);
            acc[networkKey].is_primary = acc[networkKey].is_primary || Boolean(context?.is_primary);
            if (context?.service_name && !acc[networkKey].services.includes(context.service_name)) {
                acc[networkKey].services.push(context.service_name);
            }
            const locationLabel = [
                context?.location_name,
                context?.apartment ? `кв. ${context.apartment}` : null,
                context?.personal_account ? `о/р ${context.personal_account}` : null,
            ].filter(Boolean).join(" • ");
            if (locationLabel && !acc[networkKey].locations.includes(locationLabel)) {
                acc[networkKey].locations.push(locationLabel);
            }
            return acc;
        }, {})
    );
    const hasApprovedNetworkAccess = accessNetworks.length > 0;
    const showRemovedBanner = accessOverview?.show_removed_banner === true;
    const removedNetworkName = accessOverview?.last_removed_network_name || "мережі";

    const handleDismissRemovedBanner = async () => {
        try {
            await dismissRemovedNetworkBanner();
            setAccessOverview((prev) => ({
                ...prev,
                show_removed_banner: false,
            }));
        } catch (_) {
            setAccessOverview((prev) => ({
                ...prev,
                show_removed_banner: false,
            }));
        }
    };

    const renderNetworkAccessBanners = () => (
        <>
            {showRemovedBanner ? (
                <div className="message-banner license-warning" role="status" aria-live="polite" style={{ marginBottom: 12 }}>
                    <div>
                        Вас видалено з мережі «{removedNetworkName}». Доступні приватні сенсори та приєднання за інвайт-кодом.
                    </div>
                    <button type="button" className="add-btn" onClick={handleDismissRemovedBanner} style={{ marginTop: 8 }}>
                        Зрозуміло
                    </button>
                </div>
            ) : null}
            {!hasApprovedNetworkAccess && !showRemovedBanner ? (
                <div className="message-banner license-warning" role="status" aria-live="polite" style={{ marginBottom: 12 }}>
                    Акаунт працює у self-service режимі. Зараз доступні ваш профіль, інформація про програму та приватні сенсори. Щоб відкрити повний функціонал, введіть інвайт-код мережі нижче і дочекайтесь підтвердження оператора.
                </div>
            ) : null}
        </>
    );

    const handleChangeAccessService = (value) => {
        setNewAccessRequest((prev) => ({
            ...prev,
            service_id: value,
        }));
    };

    const handleChangeInviteCode = (value) => {
        setNewAccessRequest((prev) => ({
            ...prev,
            invite_code: value,
            service_id: prev.invite_code === value ? prev.service_id : "",
        }));
        setResolvedInvite(null);
    };

    const toggleAccessPanel = (panelKey) => {
        setAccessPanels((prev) => ({
            ...prev,
            [panelKey]: !prev[panelKey],
        }));
    };

    const handleResolveInvite = async () => {
        const inviteCode = (newAccessRequest.invite_code || "").trim();
        if (!inviteCode) {
            setMessage("❌ Введіть інвайт-код мережі");
            return;
        }

        setResolvingInvite(true);
        setMessage("");
        try {
            const resolved = await resolveClientAccessInvite(inviteCode);
            setResolvedInvite(resolved);
            setNewAccessRequest((prev) => ({
                ...prev,
                invite_code: resolved.invite_code || inviteCode,
                service_id: "",
            }));
            setMessage("✅ Інвайт-код підтверджено");
        } catch (e) {
            setResolvedInvite(null);
            setMessage(`❌ ${e.message}`);
        } finally {
            setResolvingInvite(false);
        }
    };

    const handleDisconnectNetwork = async (network) => {
        if (!network?.network_id) {
            setMessage("❌ Не вдалося визначити мережу для відключення");
            return;
        }

        const confirmed = window.confirm(
            `Відключити мережу "${network.network_name}"? Після цього ви перестанете отримувати чати, новини та інші дані з цієї мережі.`,
        );
        if (!confirmed) return;

        setDisconnectingNetworkId(network.network_id);
        setMessage("");
        try {
            await disconnectClientAccessNetwork(network.network_id);
            await loadAccessOverview();
            setMessage("✅ Запит на відключення мережі відправлено оператору");
        } catch (e) {
            setMessage(`❌ ${e.message}`);
        } finally {
            setDisconnectingNetworkId(null);
        }
    };

    const handleCreateAccess = async () => {
        if (!newAccessRequest.invite_code || !resolvedInvite || !newAccessRequest.service_id) {
            setMessage("❌ Спочатку перевірте інвайт-код і оберіть сервіс");
            return;
        }

        setCreatingAccessRequest(true);
        setMessage("");
        try {
            await createClientAccessRequest({
                invite_code: (newAccessRequest.invite_code || "").trim(),
                service_id: parseInt(newAccessRequest.service_id),
                descriptions: newAccessRequest.descriptions || null,
            });
            setNewAccessRequest({
                invite_code: "",
                service_id: "",
                descriptions: "",
            });
            setResolvedInvite(null);
            await loadAccessOverview();
            setMessage("✅ Запит на підключення відправлено оператору");
        } catch (e) {
            setMessage(`❌ ${e.message}`);
        } finally {
            setCreatingAccessRequest(false);
        }
    };

    const handleSendAccessMessage = async (requestId) => {
        const body = (accessMessageDrafts[requestId] || "").trim();
        if (!body) {
            setMessage("❌ Введіть коментар для оператора");
            return;
        }

        setSendingAccessMessageId(requestId);
        setMessage("");
        try {
            await sendClientAccessRequestMessage(requestId, body);
            setAccessMessageDrafts((prev) => ({ ...prev, [requestId]: "" }));
            await loadAccessOverview();
            setMessage("✅ Коментар надіслано оператору");
        } catch (e) {
            setMessage(`❌ ${e.message}`);
        } finally {
            setSendingAccessMessageId(null);
        }
    };

    const renderAccessSection = () => (
        <div className="config-card" style={{ marginTop: 16 }}>
            <h3 style={{ marginTop: 0 }}>Сервіси, мережі та запити на підключення</h3>
            <div className="operator-inline-help" style={{ marginBottom: 12 }}>
                {hasApprovedNetworkAccess
                    ? "Тут можна переглянути всі підключені мережі та сервіси, подати запит на відключення окремої мережі або створити новий запит."
                    : "Ваш акаунт ще не підключений до жодної мережі. Тут можна ввести інвайт-код, подати запит оператору і стежити за його статусом."}
            </div>

            <div className="operator-section-stack">
                <div className="operator-collapsible-panel">
                    <button
                        type="button"
                        className={`operator-summary-chip operator-summary-chip--open operator-summary-chip--button operator-toggle-button ${accessPanels.networks ? "is-active is-open" : ""}`}
                        onClick={() => toggleAccessPanel("networks")}
                    >
                        <strong>Підключені мережі</strong> {accessNetworks.length}
                        <span className="operator-toggle-caret" aria-hidden="true">{accessPanels.networks ? "▼" : "▶"}</span>
                    </button>
                    {accessPanels.networks ? (
                        <div className="operator-collapsible-panel__body operator-section-stack">
                            {accessNetworks.length > 0 ? accessNetworks.map((network) => (
                                <div key={network.network_id || network.network_name} className="operator-accordion-item">
                                    <div className="operator-accordion-body" style={{ borderTop: 0 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                                            <div>
                                                <div><strong>{network.network_name}</strong></div>
                                                <div className="operator-inline-help">
                                                    {network.is_primary ? "Основна мережа" : "Додаткова мережа"}
                                                </div>
                                            </div>
                                            {network.network_id ? (
                                                <button
                                                    className="add-btn add-btn--secondary"
                                                    type="button"
                                                    onClick={() => handleDisconnectNetwork(network)}
                                                    disabled={disconnectingNetworkId === network.network_id || !!pendingDisconnectByNetworkId[String(network.network_id)]}
                                                >
                                                    {pendingDisconnectByNetworkId[String(network.network_id)]
                                                        ? "Запит надіслано"
                                                        : disconnectingNetworkId === network.network_id
                                                            ? "Надсилання..."
                                                            : "Відмовитись від мережі"}
                                                </button>
                                            ) : null}
                                        </div>
                                        {network.services.length > 0 ? (
                                            <div style={{ marginTop: 8 }}>
                                                <strong>Сервіси:</strong> {network.services.join(", ")}
                                            </div>
                                        ) : null}
                                        {network.locations.length > 0 ? (
                                            <div style={{ marginTop: 6 }}>
                                                <strong>Локації:</strong> {network.locations.join("; ")}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            )) : <div>Поки що немає підключених мереж.</div>}
                        </div>
                    ) : null}
                </div>

                <div className="operator-collapsible-panel">
                    <button
                        type="button"
                        className={`operator-summary-chip operator-summary-chip--closed operator-summary-chip--button operator-toggle-button ${accessPanels.create ? "is-active is-open" : ""}`}
                        onClick={() => toggleAccessPanel("create")}
                    >
                        <strong>Новий запит</strong>
                        <span className="operator-toggle-caret" aria-hidden="true">{accessPanels.create ? "▼" : "▶"}</span>
                    </button>
                    {accessPanels.create ? (
                        <div className="operator-collapsible-panel__body" style={{ display: "grid", gap: 8 }}>
                            <strong>Подати запит на новий сервіс / мережу</strong>
                            <div className="operator-inline-help">
                                Введіть інвайт-код, який вам надав оператор мережі. Без коду список усіх мереж не показується.
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <input
                                    type="text"
                                    placeholder="Інвайт-код мережі"
                                    value={newAccessRequest.invite_code}
                                    onChange={(e) => handleChangeInviteCode(e.target.value.toUpperCase())}
                                    style={{ flex: 1, minWidth: 220 }}
                                />
                                <button className="add-btn" type="button" onClick={handleResolveInvite} disabled={resolvingInvite}>
                                    {resolvingInvite ? "Перевірка..." : "Перевірити код"}
                                </button>
                            </div>
                            {resolvedInvite ? (
                                <div className="operator-detail-note">
                                    <div><strong>Мережа:</strong> {resolvedInvite.network_name || `Мережа #${resolvedInvite.network_id}`}</div>
                                    <div style={{ fontSize: 12, marginTop: 4 }}>
                                        Код дійсний до: {resolvedInvite.expires_at ? new Date(resolvedInvite.expires_at).toLocaleString("uk-UA") : "-"}
                                    </div>
                                </div>
                            ) : null}
                            <select value={newAccessRequest.service_id} onChange={(e) => handleChangeAccessService(e.target.value)}>
                                <option value="">{resolvedInvite ? "Оберіть сервіс" : "Спочатку перевірте інвайт-код"}</option>
                                {filteredServices.map((service) => (
                                    <option key={service.id} value={service.id}>{service.name}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                placeholder="Коментар для оператора"
                                value={newAccessRequest.descriptions}
                                onChange={(e) => setNewAccessRequest((prev) => ({ ...prev, descriptions: e.target.value }))}
                            />
                            <div>
                                <button className="add-btn" type="button" onClick={handleCreateAccess} disabled={creatingAccessRequest}>
                                    {creatingAccessRequest ? "Відправлення..." : "Відправити запит"}
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="operator-collapsible-panel">
                    <button
                        type="button"
                        className={`operator-summary-chip operator-summary-chip--muted operator-summary-chip--button operator-toggle-button ${accessPanels.requests ? "is-active is-open" : ""}`}
                        onClick={() => toggleAccessPanel("requests")}
                    >
                        <strong>Мої запити</strong> {accessRequests.length}
                        <span className="operator-toggle-caret" aria-hidden="true">{accessPanels.requests ? "▼" : "▶"}</span>
                    </button>
                    {accessPanels.requests ? (
                        <div className="operator-collapsible-panel__body operator-section-stack">
                            {accessRequests.length > 0 ? accessRequests.map((request) => (
                                <div key={request.id} className="operator-accordion-item">
                                    <div className="operator-accordion-body" style={{ borderTop: 0 }}>
                                        <div>
                                            <strong>{request.request_type === "disconnect" ? "Відключення мережі" : "Підключення до мережі"}</strong>
                                        </div>
                                        <div>{[request.service_name, request.network_name].filter(Boolean).join(" • ")}</div>
                                        <div className="operator-inline-help">Статус: {request.status}</div>
                                        {request.review_comment ? <div style={{ marginTop: 4 }}>Коментар: {request.review_comment}</div> : null}
                                        {Array.isArray(request.messages) && request.messages.length > 0 ? (
                                            <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
                                                {request.messages.map((item) => (
                                                    <div
                                                        key={item.id}
                                                        style={{
                                                            padding: "8px 10px",
                                                            borderRadius: 8,
                                                            background: item.sender_type === "client" ? "#eff6ff" : "#f5f5f5",
                                                        }}
                                                    >
                                                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                                                            {item.sender_type === "client" ? "Ви" : "Оператор"}
                                                        </div>
                                                        <div>{item.body}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                        {request.status === "pending" ? (
                                            <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                                                <textarea
                                                    placeholder="Відповідь оператору"
                                                    value={accessMessageDrafts[request.id] || ""}
                                                    onChange={(e) => setAccessMessageDrafts((prev) => ({ ...prev, [request.id]: e.target.value }))}
                                                    rows={3}
                                                />
                                                <div>
                                                    <button
                                                        className="add-btn"
                                                        type="button"
                                                        onClick={() => handleSendAccessMessage(request.id)}
                                                        disabled={sendingAccessMessageId === request.id}
                                                    >
                                                        {sendingAccessMessageId === request.id ? "Надсилання..." : "Надіслати коментар"}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            )) : <div>Запитів ще немає.</div>}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );

    if (loading) return <p>Завантаження…</p>;

    if (!clientData) {
        return (
            <div>
                <h2>Профіль</h2>
                <p style={{ color: "red" }}>
                    ❌ Не вдалося завантажити профіль. Можливо, закінчився час дії сесії.
                </p>
            </div>
        );
    }

    // --- MOBILE VIEW ---
    if (isMobile) {
        return (
            <div className={`config-grid client-profile-page ${editing ? "client-profile-page--editing" : ""}`}>
                {renderNetworkAccessBanners()}
                {message && <p>{message}</p>}

                <div className="config-card profile-mobile-card profile-theme-card">
                    <ThemeSelector />
                </div>

                <div className="config-card profile-mobile-card profile-settings-card">
                    <div className="profile-settings-group">
                        <NetworkBrandingSettings />
                    </div>
                </div>

                {isCapacitorRuntime() && appLockReady && (
                    <div className="config-card profile-mobile-card profile-settings-card">
                        <div className="profile-settings-group">
                            <p className="profile-settings-title">Блокування додатка</p>
                            <label className="wr-app-lock-toggle">
                                <input
                                    type="checkbox"
                                    checked={appLockEnabled}
                                    onChange={handleToggleAppLock}
                                    disabled={!appLockAvailable && !appLockEnabled}
                                />
                                <span>Біометрія / ПІН</span>
                            </label>
                            {appLockMessage && <p className="profile-field-note">{appLockMessage}</p>}
                        </div>
                    </div>
                )}

                {isCapacitorRuntime() && (
                    <div className="config-card profile-mobile-card profile-settings-card">
                        <div className="profile-settings-group">
                            <p className="profile-settings-title">Погода</p>
                            <label className="wr-app-lock-toggle">
                                <input type="checkbox" checked={weatherEnabled} onChange={handleToggleWeather} />
                                <span>Показувати на головній стрічці</span>
                            </label>

                            {weatherEnabled && (
                                <div className="profile-weather-options">
                                    <div className="profile-field-row">
                                        <strong>Місто</strong>
                                        <input
                                            type="text"
                                            value={weatherCityDraft}
                                            onChange={(e) => setWeatherCityDraft(e.target.value)}
                                            onBlur={commitWeatherCity}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    commitWeatherCity();
                                                    e.currentTarget.blur();
                                                }
                                            }}
                                            list="wr-city-list-client"
                                            placeholder="Напр.: Київ"
                                        />
                                        <datalist id="wr-city-list-client">
                                            <option value="Київ" />
                                            <option value="Львів" />
                                            <option value="Одеса" />
                                            <option value="Харків" />
                                            <option value="Дніпро" />
                                            <option value="Запоріжжя" />
                                            <option value="Вінниця" />
                                            <option value="Полтава" />
                                            <option value="Чернігів" />
                                            <option value="Черкаси" />
                                            <option value="Івано-Франківськ" />
                                            <option value="Тернопіль" />
                                            <option value="Ужгород" />
                                            <option value="Луцьк" />
                                            <option value="Рівне" />
                                            <option value="Хмельницький" />
                                            <option value="Миколаїв" />
                                            <option value="Херсон" />
                                            <option value="Суми" />
                                            <option value="Житомир" />
                                            <option value="Кропивницький" />
                                        </datalist>
                                    </div>

                                    <div className="profile-toggle-stack">
                                        <label className="wr-app-lock-toggle">
                                            <input
                                                type="checkbox"
                                                checked={weatherShowWind}
                                                onChange={handleToggleWeatherShowWind}
                                            />
                                            <span>Вітер (швидкість/напрям)</span>
                                        </label>
                                        <label className="wr-app-lock-toggle">
                                            <input
                                                type="checkbox"
                                                checked={weatherShowPressure}
                                                onChange={handleToggleWeatherShowPressure}
                                            />
                                            <span>Тиск</span>
                                        </label>
                                        <label className="wr-app-lock-toggle">
                                            <input
                                                type="checkbox"
                                                checked={weatherShowHumidity}
                                                onChange={handleToggleWeatherShowHumidity}
                                            />
                                            <span>Вологість</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="config-card profile-mobile-card profile-main-card">
                    <div className={`config-actions client-card-actions ${editing ? "is-editing is-editing-inline" : "is-idle"}`}>
                        {editing ? null : (
                            <button className="client-card-action is-primary-edit" onClick={handleEdit} title="Редагувати">
                                <Edit size={18} />
                            </button>
                        )}
                    </div>
                    <div className="profile-settings-group">
                        <p className="profile-settings-title">Дані профілю</p>
                        <div className="profile-field-row">
                            <strong>Ім’я</strong>
                            {editing ? (
                                <input
                                    type="text"
                                    value={editedData.name}
                                    onChange={(e) => setEditedData({ ...editedData, name: e.target.value })}
                                />
                            ) : clientData.name}
                        </div>

                        <div className="profile-field-row">
                            <strong>Опис / адреса</strong>
                            {editing ? (
                                <input
                                    type="text"
                                    value={editedData.descriptions}
                                    onChange={(e) => setEditedData({ ...editedData, descriptions: e.target.value })}
                                />
                            ) : (clientData.descriptions || "—")}
                        </div>

                        <div className="profile-field-row">
                            <strong>Авто</strong>
                            {editing ? (
                                <input
                                    type="text"
                                    value={editedData.car_number}
                                    placeholder="Номер авто"
                                    onChange={(e) => setEditedData({ ...editedData, car_number: e.target.value })}
                                />
                            ) : (clientData.car_number || "—")}
                        </div>

                        <div className="profile-field-row">
                            <strong>Особовий рахунок</strong>
                            {clientData.personal_account || "—"}
                        </div>
                    </div>

                    {editing && (
                        <div className="profile-settings-group">
                            <p className="profile-settings-title">Зміна пароля</p>
                            <div className="profile-field-row">
                                <strong>Поточний пароль</strong>
                                <div style={{ position: "relative", width: "100%" }}>
                                    <input
                                        style={{ width: "100%", paddingRight: 44 }}
                                        type={showCurrentPassword ? "text" : "password"}
                                        value={editedData.current_password}
                                        onChange={(e) => setEditedData({ ...editedData, current_password: e.target.value })}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowCurrentPassword((v) => !v)}
                                        title={showCurrentPassword ? "Приховати пароль" : "Показати пароль"}
                                        aria-label={showCurrentPassword ? "Приховати пароль" : "Показати пароль"}
                                        style={{
                                            position: "absolute",
                                            right: 10,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            background: "transparent",
                                            border: "none",
                                            padding: 0,
                                            cursor: "pointer",
                                            color: "#333",
                                            zIndex: 2,
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>
                            <div className="profile-field-row">
                                <strong>Новий пароль</strong>
                                <div style={{ position: "relative", width: "100%" }}>
                                    <input
                                        style={{ width: "100%", paddingRight: 44 }}
                                        type={showNewPassword ? "text" : "password"}
                                        value={editedData.new_password}
                                        onChange={(e) => setEditedData({ ...editedData, new_password: e.target.value })}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword((v) => !v)}
                                        title={showNewPassword ? "Приховати пароль" : "Показати пароль"}
                                        aria-label={showNewPassword ? "Приховати пароль" : "Показати пароль"}
                                        style={{
                                            position: "absolute",
                                            right: 10,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            background: "transparent",
                                            border: "none",
                                            padding: 0,
                                            cursor: "pointer",
                                            color: "#333",
                                            zIndex: 2,
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {editing ? (
                    <div className="profile-floating-actions" role="toolbar" aria-label="Дії редагування">
                        <button className="profile-floating-action is-cancel" onClick={handleCancel} title="Скасувати" aria-label="Скасувати">
                            <X size={20} />
                        </button>
                        <button className="profile-floating-action is-primary-save" onClick={handleSave} title="Зберегти" aria-label="Зберегти">
                            <Save size={20} />
                        </button>
                    </div>
                ) : null}

                {renderAccessSection()}

                <div className="config-card profile-logout-card">
                    <strong>Сесія</strong>
                    <p>Вихід із акаунта. Після виходу потрібно знову увійти.</p>
                    <button
                        type="button"
                        className="profile-logout-btn"
                        onClick={handleLogout}
                        disabled={loggingOut}
                    >
                        <LogOut size={16} aria-hidden="true" />
                        {loggingOut ? "Вихід..." : "Вийти з акаунта"}
                    </button>
                </div>

                <div className="config-card profile-delete-card">
                    <div className="profile-delete-card__head">
                        <span aria-hidden="true">⚠️</span>
                        <strong>Видалення акаунту</strong>
                    </div>
                    <p>
                        Видалення акаунту є незворотною дією. Усі персональні дані цього клієнта будуть видалені.
                    </p>
                    <button
                        type="button"
                        className="profile-logout-btn"
                        onClick={handleDeleteAccount}
                        style={{ background: "#b42318", borderColor: "#b42318", color: "#fff" }}
                    >
                        <Trash2 size={16} aria-hidden="true" />
                        Видалити акаунт
                    </button>
                </div>
            </div>
        );
    }

    // --- DESKTOP VIEW ---
    return (
        <div>
            <h2>Редагування профілю</h2>
            {renderNetworkAccessBanners()}
            {message && <p>{message}</p>}

            <div className="table-responsive">
                <table>
                    <thead>
                        <tr>
                            <th>Поле</th>
                            <th>Значення</th>
                            <th>Дії</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Ім’я</td>
                            <td>
                                {editing ? (
                                    <input
                                        type="text"
                                        value={editedData.name}
                                        onChange={(e) => setEditedData({ ...editedData, name: e.target.value })}
                                    />
                                ) : clientData.name}
                            </td>
                            <td rowSpan={isCapacitorRuntime() && appLockReady ? 8 : 7}>
                                {editing ? (
                                    <>
                                        <button onClick={handleSave} title="Зберегти">
                                            <Save size={18} />
                                        </button>
                                        <button onClick={handleCancel} title="Відмінити" style={{ marginLeft: 6 }}>
                                            <X size={18} />
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button onClick={handleEdit} title="Редагувати">
                                            <Edit size={18} />
                                        </button>
                                        <button onClick={handleDeleteAccount} title="Видалити акаунт" style={{ marginLeft: 6, backgroundColor: 'red', color: 'white' }}>
                                            Видалити акаунт
                                        </button>
                                    </>
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td>Тема додатка</td>
                            <td>
                                <ThemeSelector compact />
                            </td>
                        </tr>
                        <tr>
                            <td>Логотип мережі</td>
                            <td>
                                <NetworkBrandingSettings compact />
                            </td>
                        </tr>
                        {isCapacitorRuntime() && appLockReady && (
                            <tr>
                                <td>Блокування додатка</td>
                                <td>
                                    <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <input
                                            type="checkbox"
                                            checked={appLockEnabled}
                                            onChange={handleToggleAppLock}
                                            disabled={!appLockAvailable && !appLockEnabled}
                                        />
                                        <span>Біометрія / ПІН</span>
                                    </label>
                                    {appLockMessage && <div>{appLockMessage}</div>}
                                </td>
                            </tr>
                        )}
                        <tr>
                            <td>Опис / адреса</td>
                            <td>
                                {editing ? (
                                    <input
                                        type="text"
                                        value={editedData.descriptions}
                                        onChange={(e) => setEditedData({ ...editedData, descriptions: e.target.value })}
                                    />
                                ) : clientData.descriptions}
                            </td>
                        </tr>
                        <tr>
                            <td>Авто</td>
                            <td>
                                {editing ? (
                                    <input
                                        type="text"
                                        value={editedData.car_number}
                                        placeholder="Номер авто"
                                        onChange={(e) => setEditedData({ ...editedData, car_number: e.target.value })}
                                    />
                                ) : (clientData.car_number || "-")}
                            </td>
                        </tr>
                        <tr>
                            <td>Особовий рахунок</td>
                            <td>{clientData.personal_account || "-"}</td>
                        </tr>
                        <tr>
                            <td>Поточний пароль</td>
                            <td>
                                {editing && (
                                    <div style={{ position: "relative", width: "100%" }}>
                                        <input
                                            style={{ width: "100%", paddingRight: 44 }}
                                            type={showCurrentPassword ? "text" : "password"}
                                            value={editedData.current_password}
                                            onChange={(e) => setEditedData({ ...editedData, current_password: e.target.value })}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowCurrentPassword((v) => !v)}
                                            title={showCurrentPassword ? "Приховати пароль" : "Показати пароль"}
                                            aria-label={showCurrentPassword ? "Приховати пароль" : "Показати пароль"}
                                            style={{
                                                position: "absolute",
                                                right: 10,
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                background: "transparent",
                                                border: "none",
                                                padding: 0,
                                                cursor: "pointer",
                                                color: "#333",
                                                zIndex: 2,
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td>Новий пароль</td>
                            <td>
                                {editing && (
                                    <div style={{ position: "relative", width: "100%" }}>
                                        <input
                                            style={{ width: "100%", paddingRight: 44 }}
                                            type={showNewPassword ? "text" : "password"}
                                            value={editedData.new_password}
                                            onChange={(e) => setEditedData({ ...editedData, new_password: e.target.value })}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword((v) => !v)}
                                            title={showNewPassword ? "Приховати пароль" : "Показати пароль"}
                                            aria-label={showNewPassword ? "Приховати пароль" : "Показати пароль"}
                                            style={{
                                                position: "absolute",
                                                right: 10,
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                background: "transparent",
                                                border: "none",
                                                padding: 0,
                                                cursor: "pointer",
                                                color: "#333",
                                                zIndex: 2,
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {renderAccessSection()}

            <div className="profile-logout-card">
                <strong>Сесія</strong>
                <p>Вихід із акаунта. Після виходу потрібно знову увійти.</p>
                <button
                    type="button"
                    className="profile-logout-btn"
                    onClick={handleLogout}
                    disabled={loggingOut}
                >
                    <LogOut size={16} aria-hidden="true" />
                    {loggingOut ? "Вихід..." : "Вийти з акаунта"}
                </button>
            </div>
        </div>
    );
};

export default ClientProfile;
